/**
 * Hook centralisé pour récupérer les rôles approuvés
 * Évite les appels multiples en partageant un cache global
 */

import { useState, useEffect, useRef } from "react"

// Cache global partagé entre tous les composants
interface CacheEntry {
  roles: string[]
  timestamp: number
  promise?: Promise<string[]>
}

const globalCache = new Map<string, CacheEntry>()
const CACHE_DURATION = 30000 // 30 secondes

/**
 * Hook pour récupérer les rôles approuvés de l'utilisateur
 * Utilise un cache global pour éviter les appels multiples
 */
export function useApprovedRoles(options: {
  enabled?: boolean
  onSuccess?: (roles: string[]) => void
  onError?: (error: Error) => void
} = {}) {
  const { enabled = true, onSuccess, onError } = options
  const [approvedRoles, setApprovedRoles] = useState<string[]>([])
  const [loading, setLoading] = useState(true) // Commencer à true pour indiquer qu'on charge
  const [error, setError] = useState<Error | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Si on arrive depuis l'email d'approbation de rôle (lien avec ?refreshRoles=1),
  // on invalide le cache des rôles approuvés et on retire le param de l'URL pour
  // que l'utilisateur voie ses nouvelles permissions sans recharger manuellement.
  useEffect(() => {
    if (typeof window === "undefined") return
    const url = new URL(window.location.href)
    if (url.searchParams.get("refreshRoles") !== "1") return

    globalCache.delete("approved-roles")
    url.searchParams.delete("refreshRoles")
    window.history.replaceState(
      window.history.state,
      "",
      url.pathname + (url.search ? url.search : "") + url.hash
    )
  }, [])

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      setApprovedRoles([])
      return
    }

    const cacheKey = "approved-roles"
    const cached = globalCache.get(cacheKey)

    // Si on a un cache valide, l'utiliser immédiatement
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      if (mountedRef.current) {
        if (process.env.NODE_ENV === "development") {
          console.log(
            `[useApprovedRoles] Using cached roles:`,
            {
              roles: cached.roles,
              rolesCount: cached.roles.length,
              cacheAge: Date.now() - cached.timestamp,
            }
          )
        }
        // Utiliser setTimeout pour s'assurer que l'état est mis à jour de manière asynchrone
        // Cela permet aux composants de recevoir la mise à jour même si le cache est utilisé immédiatement
        setTimeout(() => {
          if (mountedRef.current) {
            setApprovedRoles(cached.roles)
            setLoading(false)
            setError(null)
            onSuccess?.(cached.roles)
          }
        }, 0)
      }
      return
    }

    // Si une requête est déjà en cours, attendre sa résolution
    if (cached?.promise) {
      setLoading(true)
      cached.promise
        .then((roles) => {
          if (mountedRef.current) {
            setApprovedRoles(roles)
            setLoading(false)
            setError(null)
            onSuccess?.(roles)
          }
        })
        .catch((err) => {
          if (mountedRef.current) {
            setError(err)
            setLoading(false)
            onError?.(err)
          }
        })
      return
    }

    // Sinon, faire une nouvelle requête
    setLoading(true)
    setError(null)

    const fetchPromise = fetch("/api/auth/nextauth-drupal/drupal/approved-roles", {
      cache: "no-store",
    })
      .then(async (response) => {
        if (!response.ok) {
          // Même si l'API retourne une erreur, essayer de parser le JSON
          // car l'API peut retourner 200 avec un fallback
          try {
            const data = await response.json()
            return data.approved_roles || []
          } catch {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`)
          }
        }
        const data = await response.json()
        const roles = data.approved_roles || []
        
        // Log pour déboguer le format des rôles
        if (process.env.NODE_ENV === "development") {
          console.log(
            "[useApprovedRoles] Rôles reçus de l'API:",
            {
              raw: roles,
              types: roles.map((r: any) => typeof r),
              sample: roles.slice(0, 3),
            }
          )
        }
        
        return roles
      })
      .then((roles) => {
        // Mettre à jour le cache global AVANT de retourner
        // Cela permet aux autres composants d'utiliser le cache immédiatement
        globalCache.set(cacheKey, {
          roles,
          timestamp: Date.now(),
        })
        
        if (process.env.NODE_ENV === "development") {
          console.log(
            `[useApprovedRoles] Cache mis à jour avec ${roles.length} rôles:`,
            roles
          )
        }
        
        return roles
      })
      .catch((err) => {
        // En cas d'erreur, mettre un cache vide pour éviter les appels répétés
        globalCache.set(cacheKey, {
          roles: [],
          timestamp: Date.now(),
        })
        throw err
      })

    // Stocker la promesse dans le cache pour que d'autres composants puissent l'attendre
    globalCache.set(cacheKey, {
      roles: cached?.roles || [],
      timestamp: cached?.timestamp || 0,
      promise: fetchPromise,
    })

    fetchPromise
      .then((roles) => {
        // S'assurer que le cache est bien mis à jour
        globalCache.set(cacheKey, {
          roles,
          timestamp: Date.now(),
        })
        
        // Nettoyer la promesse du cache une fois résolue
        const entry = globalCache.get(cacheKey)
        if (entry) {
          entry.promise = undefined
        }
        
        if (mountedRef.current) {
          if (process.env.NODE_ENV === "development") {
            console.log(
              `[useApprovedRoles] Mise à jour de l'état avec ${roles.length} rôles:`,
              roles
            )
          }
          setApprovedRoles(roles)
          setLoading(false)
          setError(null)
          onSuccess?.(roles)
        }
      })
      .catch((err) => {
        // Nettoyer la promesse du cache en cas d'erreur
        const entry = globalCache.get(cacheKey)
        if (entry) {
          entry.promise = undefined
        }
        if (mountedRef.current) {
          setError(err)
          setLoading(false)
          onError?.(err)
        }
      })
  }, [enabled, onSuccess, onError])

  /**
   * Fonction pour invalider le cache et forcer un refresh
   */
  const invalidateCache = () => {
    globalCache.delete("approved-roles")
    if (enabled && mountedRef.current) {
      setLoading(true)
      // Le useEffect va détecter l'absence de cache et refaire une requête
    }
  }

  return {
    approvedRoles,
    loading,
    error,
    invalidateCache,
  }
}

/**
 * Fonction utilitaire pour invalider le cache depuis l'extérieur
 */
export function invalidateApprovedRolesCache() {
  globalCache.delete("approved-roles")
}

